import { useState } from "react";
import CalculatorLayout from "@/components/CalculatorLayout";
import { ResultCard } from "@/components/ResultCard";
import AgeGenderFields from "@/components/AgeGenderFields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { round, parseNum, ftInToCm, lbToKg } from "@/calculators/_helpers";

// ---------- GFR (CKD-EPI 2021 race-free) ----------
export function GfrCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [creat, setCreat] = useState("");
  const a = parseNum(age), cr = parseNum(creat);
  let egfr = 0;
  if (a > 0 && cr > 0) {
    const k = gender === "male" ? 0.9 : 0.7;
    const alpha = gender === "male" ? -0.302 : -0.241;
    const genderFactor = gender === "male" ? 1 : 1.012;
    const ratio = cr / k;
    const minPart = Math.min(ratio, 1) ** alpha;
    const maxPart = Math.max(ratio, 1) ** -1.200;
    egfr = 142 * minPart * maxPart * (0.9938 ** a) * genderFactor;
  }
  const egfrR = round(egfr, 1);
  const stage =
    egfr === 0 ? null :
    egfr >= 90 ? { label: "G1 — Normal or high", tone: "good" } :
    egfr >= 60 ? { label: "G2 — Mildly decreased", tone: "good" } :
    egfr >= 45 ? { label: "G3a — Mild-to-moderate decrease", tone: "warning" } :
    egfr >= 30 ? { label: "G3b — Moderate-to-severe decrease", tone: "warning" } :
    egfr >= 15 ? { label: "G4 — Severely decreased", tone: "danger" } :
    { label: "G5 — Kidney failure", tone: "danger" };
  return (
    <CalculatorLayout
      calc={calc}
      intro="Estimated glomerular filtration rate using the CKD-EPI 2021 race-free equation."
      resultText={egfr > 0 ? `eGFR ${egfrR} mL/min/1.73 m² — ${stage.label}.` : null}
      formula={<p>CKD-EPI 2021 (Inker et al., NEJM) removes the race coefficient. Uses gender, age and serum creatinine (mg/dL). See <em>DOI: 10.1056/NEJMoa2102953</em>.</p>}
      faqs={[
        { q: "Is my creatinine in mg/dL or µmol/L?", a: "US labs use mg/dL; many others use µmol/L. Divide µmol/L by 88.4 to convert to mg/dL." },
        { q: "Does eGFR replace the need for a doctor?", a: "No. eGFR is one of many measures used to evaluate kidney health." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="space-y-2"><Label>Serum creatinine (mg/dL)</Label>
          <Input type="number" step="0.01" data-testid="gfr-creat" value={creat} onChange={(e) => setCreat(e.target.value)} placeholder="e.g. 0.9" /></div>
      </div>
      {egfr > 0 && (
        <div className="mt-6"><ResultCard label="eGFR" value={egfrR} unit="mL/min/1.73 m²" tone={stage.tone} testId="gfr-result">{stage.label}</ResultCard></div>
      )}
    </CalculatorLayout>
  );
}

// ---------- BSA (Mosteller) ----------
export function BsaCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [unit, setUnit] = useState("metric");
  const [heightCm, setHeightCm] = useState("");
  const [ft, setFt] = useState("");
  const [inches, setInches] = useState("");
  const [weight, setWeight] = useState("");
  const h_cm = unit === "metric" ? parseNum(heightCm) : ftInToCm(ft, inches);
  const w_kg = unit === "metric" ? parseNum(weight) : lbToKg(parseNum(weight));
  const bsa = h_cm > 0 && w_kg > 0 ? Math.sqrt((h_cm * w_kg) / 3600) : 0;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Body surface area (BSA) using the Mosteller formula — widely used for medication dosing."
      resultText={bsa > 0 ? `BSA: ${round(bsa, 2)} m².` : null}
      formula={<p><strong>Mosteller:</strong> BSA (m²) = √(height cm × weight kg ÷ 3600).</p>}
      faqs={[
        { q: "Which BSA formula is best?", a: "Mosteller is the most commonly used clinically for its simplicity and accuracy. Du Bois and Haycock are alternatives." },
        { q: "What's a normal BSA?", a: "About 1.6–2.0 m² for most adults, though it varies with size." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <RadioGroup value={unit} onValueChange={setUnit} className="flex gap-2">
          {["metric", "imperial"].map((u) => (
            <div key={u} className="flex-1">
              <RadioGroupItem value={u} id={`bsu-${u}`} className="peer sr-only" />
              <Label htmlFor={`bsu-${u}`} className="flex h-10 cursor-pointer items-center justify-center rounded-full border border-border text-sm font-medium peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary">{u === "metric" ? "Metric" : "Imperial"}</Label>
            </div>
          ))}
        </RadioGroup>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Height</Label>
            {unit === "metric"
              ? <Input type="number" data-testid="bsa-h" placeholder="cm" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
              : <div className="grid grid-cols-2 gap-2">
                  <Input type="number" data-testid="bsa-ft" placeholder="ft" value={ft} onChange={(e) => setFt(e.target.value)} />
                  <Input type="number" data-testid="bsa-in" placeholder="in" value={inches} onChange={(e) => setInches(e.target.value)} />
                </div>}
          </div>
          <div className="space-y-2"><Label>Weight ({unit === "metric" ? "kg" : "lb"})</Label>
            <Input type="number" data-testid="bsa-w" value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
        </div>
      </div>
      {bsa > 0 && <div className="mt-6"><ResultCard label="Body surface area" value={round(bsa, 2)} unit="m²" testId="bsa-result" /></div>}
    </CalculatorLayout>
  );
}

// ---------- BAC (Widmark) ----------
const DRINK_GRAMS = 14;
export function BacCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [unit, setUnit] = useState("metric");
  const [weight, setWeight] = useState("");
  const [drinks, setDrinks] = useState("");
  const [hours, setHours] = useState("");
  const kg = unit === "metric" ? parseNum(weight) : lbToKg(parseNum(weight));
  const grams = parseNum(drinks) * DRINK_GRAMS;
  const r = gender === "male" ? 0.68 : 0.55;
  const rawBac = kg > 0 && grams > 0 ? (grams * 100) / (kg * 1000 * r) - 0.015 * parseNum(hours) : 0;
  const bac = Math.max(0, rawBac);
  const status =
    bac === 0 ? null :
    bac < 0.02 ? { label: "Minimal impairment", tone: "good" } :
    bac < 0.05 ? { label: "Mild — do not drive", tone: "warning" } :
    bac < 0.08 ? { label: "Impaired", tone: "warning" } :
    bac < 0.15 ? { label: "Legally intoxicated (US)", tone: "danger" } :
    { label: "Severely impaired", tone: "danger" };
  return (
    <CalculatorLayout
      calc={calc}
      intro="Estimated blood alcohol content using Widmark's formula. Never drink and drive."
      resultText={bac > 0 ? `Estimated BAC: ${round(bac, 3)}% — ${status.label}.` : null}
      formula={<p><strong>Widmark:</strong> BAC = (alcohol grams × 100) ÷ (weight g × r) − 0.015 × hours. r = 0.68 (men), 0.55 (women). One US standard drink = 14 g ethanol.</p>}
      faqs={[
        { q: "How accurate is this?", a: "It's a rough estimate — real BAC varies with food, medications, genetics, hydration and more. Never rely on it to decide whether to drive." },
        { q: "What is the legal limit?", a: "In the US it's 0.08% for adult drivers; many countries use 0.05% or lower. Zero-tolerance applies for commercial drivers and under-21s." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <RadioGroup value={unit} onValueChange={setUnit} className="flex gap-2">
          {["metric", "imperial"].map((u) => (
            <div key={u} className="flex-1">
              <RadioGroupItem value={u} id={`bacu-${u}`} className="peer sr-only" />
              <Label htmlFor={`bacu-${u}`} className="flex h-10 cursor-pointer items-center justify-center rounded-full border border-border text-sm font-medium peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary">{u === "metric" ? "kg" : "lb"}</Label>
            </div>
          ))}
        </RadioGroup>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2"><Label>Weight</Label><Input type="number" data-testid="bac-w" value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
          <div className="space-y-2"><Label>Standard drinks (14g each)</Label><Input type="number" step="0.5" data-testid="bac-drinks" value={drinks} onChange={(e) => setDrinks(e.target.value)} /></div>
          <div className="space-y-2"><Label>Hours since first drink</Label><Input type="number" step="0.25" data-testid="bac-hours" value={hours} onChange={(e) => setHours(e.target.value)} /></div>
        </div>
      </div>
      {bac > 0 && (
        <div className="mt-6"><ResultCard label="Estimated BAC" value={`${round(bac, 3)}%`} tone={status.tone} testId="bac-result">{status.label} — Legal driving limit varies (0.05–0.08% typical).</ResultCard></div>
      )}
    </CalculatorLayout>
  );
}
